import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useRecentUpdates } from '../../hooks/useLatestUpdate';
import { LatestUpdate } from '../../services/api/updatesService';
import { useMetaLang } from '../../i18n/metaReviewLang';

/**
 * ログイン後の home で表示する、プロダクトアップデートのカルーセル通知。
 *
 * - 未読のアップデートがある時だけ表示
 * - 直近 N 件を横スクロールカルーセルで見せる（シンプル構成・画像なし）
 * - 左右ボタン / ページネーションドット / キーボード ← → 対応
 * - 「ブログで全て見る」で lp.tasky-ai.com/updates に新規タブ遷移
 * - 閉じる / 最後のページの CTA 押下で localStorage に既読 mark
 */
const UpdateAnnouncementModal: React.FC = () => {
  const [lang] = useMetaLang();
  const { updates, hasUnread, markAllAsSeen } = useRecentUpdates(5);
  const [activeIndex, setActiveIndex] = useState(0);
  const shouldDisplay = lang !== 'en' && hasUnread;

  useEffect(() => {
    setActiveIndex(0);
  }, [updates]);

  const handleClose = useCallback(() => {
    markAllAsSeen();
  }, [markAllAsSeen]);

  const goPrev = useCallback(() => {
    setActiveIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((i) => Math.min(updates.length - 1, i + 1));
  }, [updates.length]);

  const openAllUpdates = useCallback(() => {
    const lpBase = updates[0]?.lp_url.split('/updates/')[0] ?? 'https://lp.tasky-ai.com';
    window.open(`${lpBase}/updates`, '_blank', 'noopener,noreferrer');
    markAllAsSeen();
  }, [updates, markAllAsSeen]);

  const openCurrentDetail = useCallback(() => {
    const current = updates[activeIndex];
    if (!current) return;
    window.open(current.lp_url, '_blank', 'noopener,noreferrer');
    markAllAsSeen();
  }, [updates, activeIndex, markAllAsSeen]);

  useEffect(() => {
    if (!shouldDisplay) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shouldDisplay, goPrev, goNext, handleClose]);

  if (!shouldDisplay || updates.length === 0) {
    return null;
  }

  const isLast = activeIndex === updates.length - 1;
  const isFirst = activeIndex === 0;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-modal-title"
    >
      <div
        className="relative w-full rounded-2xl bg-white shadow-2xl flex flex-col"
        style={{ maxWidth: '480px' }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="閉じる"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-600 shadow-sm hover:bg-white hover:text-gray-900 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Carousel track */}
        <div className="overflow-hidden rounded-t-2xl">
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {updates.map((u) => (
              <div
                key={u.id}
                style={{ flex: '0 0 100%', minWidth: '100%', maxWidth: '100%' }}
              >
                <UpdateSlide update={u} />
              </div>
            ))}
          </div>
        </div>

        {/* Prev / Next overlay buttons */}
        {!isFirst && (
          <button
            type="button"
            onClick={goPrev}
            aria-label="前のアップデート"
            className="absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow-md hover:bg-white transition"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {!isLast && (
          <button
            type="button"
            onClick={goNext}
            aria-label="次のアップデート"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow-md hover:bg-white transition"
          >
            <ChevronRight size={20} />
          </button>
        )}

        {/* Footer: pagination + CTA */}
        <div className="px-6 pb-5 border-t border-gray-100">
          <div className="flex justify-center gap-1.5 pt-3 pb-4">
            {updates.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIndex(i)}
                aria-label={`スライド ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === activeIndex ? 'w-6 bg-gray-900' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={openAllUpdates}
              className="text-sm font-medium text-gray-500 hover:text-gray-800 underline-offset-2 hover:underline"
            >
              ブログで全て見る
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={openCurrentDetail}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:opacity-90 transition-opacity"
              >
                詳しく見る
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:opacity-90 transition-opacity"
              >
                次へ
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// Slide (画像なし、シンプル構成)

interface UpdateSlideProps {
  update: LatestUpdate;
}

const UpdateSlide: React.FC<UpdateSlideProps> = ({ update }) => {
  const badge = resolveBadgeMeta(update.type);

  return (
    <div className="px-7 pt-12 pb-6">
      <div className="flex items-center gap-3 mb-4">
        <span
          className={`inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${badge.className}`}
        >
          {badge.label}
        </span>
        <span className="text-xs font-medium text-gray-500">
          {formatDate(update.published_at)}
        </span>
      </div>
      <h2
        id="update-modal-title"
        className="text-xl font-extrabold leading-snug text-gray-900 mb-3"
      >
        {update.title}
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed min-h-[4.5rem]">
        {update.summary ?? '新しいアップデートが公開されました。詳細はブログをご覧ください。'}
      </p>
    </div>
  );
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

function resolveBadgeMeta(
  type: LatestUpdate['type'],
): { label: string; className: string } {
  switch (type) {
    case 'new_feature':
      return { label: 'NEW', className: 'bg-[#dff5ed] text-[#2a8c6a]' };
    case 'improvement':
      return { label: 'IMPROVE', className: 'bg-[#e0f0fa] text-[#1e70b8]' };
    case 'fix':
      return { label: 'FIX', className: 'bg-[#fdf0d5] text-[#a07830]' };
    default:
      return { label: 'UPDATE', className: 'bg-gray-100 text-gray-600' };
  }
}

export default UpdateAnnouncementModal;
